<template>
  <div class="container">
    <!-- Header -->
    <div class="form-header">
      <button class="btn-back" @click="cancelar">
        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        Volver
      </button>
      <div class="header-center">
        <h1 class="title">{{ isEditMode ? 'Editar Item' : 'Nuevo Item' }}</h1>
        <span class="subtitle">{{ isEditMode ? `Código: ${codigo}` : 'Nuevo producto o servicio' }}</span>
      </div>
      <div class="header-right"></div>
    </div>

    <!-- Tabs -->
    <div class="tabs-bar">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        :class="['tab-btn', activeTab === tab.id ? 'tab-active' : '']"
        @click="activeTab = tab.id"
      >
        {{ tab.nombre }}
      </button>
    </div>

    <div class="form-body">

      <!-- TAB: GENERAL -->
      <div v-show="activeTab === 'general'">
        <section class="card mb">
          <div class="section-title">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="12" y1="16" x2="12" y2="16"/></svg>
            Información Básica
          </div>
          <div class="grid-2">
            <div class="field full-width">
              <label>Nombre / Modelo</label>
              <input v-model="producto.nombre" type="text" placeholder="Ej: IPHONE 13 PRO MAX"/>
              <span v-if="errores.nombre" class="field-error">{{ errores.nombre }}</span>
            </div>
            <div class="field full-width">
              <label>Descripción</label>
              <textarea v-model="producto.descripcion" rows="3" placeholder="Detalles técnicos, características..."></textarea>
            </div>
          </div>
        </section>

        <section class="card mb">
          <div class="section-title">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>
            Códigos
          </div>
          <div class="grid-3">
            <div class="field">
              <label>Código (Auto)</label>
              <input :value="codigo || '—'" type="text" disabled class="field-disabled"/>
            </div>
            <div class="field">
              <label>Código Auxiliar</label>
              <input v-model="producto.codigo_auxiliar" type="text" placeholder="00000111"/>
            </div>
            <div class="field">
              <label>Estado</label>
              <select v-model="producto.estado">
                <option value="ACTIVO">ACTIVO</option>
                <option value="INACTIVO">INACTIVO</option>
              </select>
            </div>
          </div>
        </section>
      </div>

      <!-- TAB: CLASIFICACIÓN -->
      <div v-show="activeTab === 'clasificacion'">
        <section class="card mb">
          <div class="section-title">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2z"/></svg>
            Clasificación
          </div>
          <div class="grid-3">
            <div class="field">
              <label>Unidad</label>
              <select v-model="producto.unidad">
                <option v-for="u in unidades" :key="u" :value="u">{{ u }}</option>
              </select>
            </div>
            <div class="field">
              <label>Tipo</label>
              <select v-model="producto.tipo">
                <option value="PRODUCTO">PRODUCTO</option>
                <option value="SERVICIO">SERVICIO</option>
              </select>
            </div>
            <div class="field">
              <label>Categoría</label>
              <input v-model="producto.categoria" type="text" placeholder="Ej: ELECTRÓNICA"/>
            </div>
            <div class="field">
              <label>Marca</label>
              <input v-model="producto.marca" type="text" placeholder="Ej: SAMSUNG"/>
            </div>
          </div>
        </section>

        <section class="card">
          <div class="section-title">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
            Flags de Publicación
          </div>
          <div class="flags-grid">
            <label v-for="flag in flags" :key="flag.campo" class="flag-item">
              <div class="flag-info">
                <span class="flag-name">{{ flag.nombre }}</span>
              </div>
              <div class="toggle-wrapper">
                <input v-model="producto[flag.campo]" type="checkbox" class="toggle-input"/>
                <div class="toggle-track">
                  <div class="toggle-thumb"></div>
                </div>
              </div>
            </label>
          </div>
        </section>
      </div>

      <!-- TAB: PRECIOS -->
      <div v-show="activeTab === 'precios'">
        <section class="card">
          <div class="section-title">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            Costos y Precios
          </div>

          <div class="grid-3 mb">
            <div class="field">
              <label>Costo</label>
              <div class="input-prefix">
                <span>$</span>
                <input v-model.number="producto.costo" @input="calcularPrecios" type="number" placeholder="0.00" min="0" step="0.01"/>
              </div>
            </div>
            <div class="field">
              <label>Utilidad %</label>
              <div class="input-suffix">
                <input v-model.number="producto.utilidad" @input="calcularPrecios" type="number" placeholder="0.00" min="0" step="0.01"/>
                <span>%</span>
              </div>
            </div>
            <div class="field">
              <label>Ganancia</label>
              <div class="input-prefix">
                <span>$</span>
                <input :value="gananciaCalculada" type="number" disabled class="field-disabled"/>
              </div>
            </div>
            <div class="field">
              <label>Precio Unitario</label>
              <div class="input-prefix">
                <span>$</span>
                <input :value="producto.precio_unitario" type="number" disabled class="field-disabled field-highlight"/>
              </div>
            </div>
            <div class="field">
              <label>IVA aplicado</label>
              <div class="input-suffix">
                <input :value="ivaSeleccionado.tarifa" type="number" disabled class="field-disabled"/>
                <span>%</span>
              </div>
            </div>
            <div class="field">
              <label>Precio + IVA</label>
              <div class="input-prefix">
                <span>$</span>
                <input :value="producto.precio_con_iva" type="number" disabled class="field-disabled field-highlight"/>
              </div>
            </div>
          </div>

          <div class="info-box">
            Los precios se calculan automáticamente según costo, utilidad e impuesto seleccionado
          </div>
        </section>
      </div>

      <!-- TAB: IMPUESTOS -->
      <div v-show="activeTab === 'impuestos'">
        <section class="card mb">
          <div class="section-title">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            Seleccionar Impuesto
          </div>
          <div class="field" style="max-width: 400px">
            <label>Impuesto</label>
            <select v-model="producto.codigo_iva" @change="calcularPrecios">
              <option v-for="imp in impuestosOpciones" :key="imp.codigo" :value="imp.codigo">
                {{ imp.label }}
              </option>
            </select>
          </div>
        </section>

        <section class="card">
          <div class="section-title">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>
            Tabla de Impuestos SRI
          </div>
          <div class="table-wrapper">
            <table class="items-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th class="text-center">Cód. %</th>
                  <th class="text-center">Tarifa %</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="imp in impuestosOpciones"
                  :key="imp.codigo"
                  :class="['table-row', producto.codigo_iva === imp.codigo ? 'row-selected' : '']"
                  @click="seleccionarImpuesto(imp)"
                >
                  <td><span class="code-badge">{{ imp.codigo }}</span></td>
                  <td>{{ imp.label }}</td>
                  <td class="text-center">{{ imp.codigoPorcentaje }}</td>
                  <td class="text-center"><strong>{{ imp.tarifa }}%</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>

    </div>

    <!-- Footer de acciones -->
    <div class="form-footer">
      <button
        class="btn-save"
        :disabled="loading"
        @click="guardar"
      >
        <svg v-if="!loading" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
          <polyline points="17 21 17 13 7 13 7 21"/>
          <polyline points="7 3 7 8 15 8"/>
        </svg>
        {{ loading ? 'Guardando...' : (isEditMode ? 'Actualizar Item' : 'Guardar Item') }}
      </button>
      <button class="btn-cancel" @click="cancelar">Cancelar</button>
    </div>

    <div v-if="feedback.mensaje" :class="['feedback', feedback.tipo]">{{ feedback.mensaje }}</div>
  </div>
</template>

<script>
import ApiService from '../../Servicios/ApiService'

const IMPUESTOS = [
  { codigo: 0, codigoPorcentaje: 0, label: 'Sin IVA (0%)',       tarifa: 0  },
  { codigo: 2, codigoPorcentaje: 2, label: 'IVA 12%',             tarifa: 12 },
  { codigo: 4, codigoPorcentaje: 4, label: 'IVA 15%',             tarifa: 15 },
  { codigo: 5, codigoPorcentaje: 5, label: 'IVA 5%',              tarifa: 5  },
  { codigo: 7, codigoPorcentaje: 7, label: 'No Objeto de Impuesto', tarifa: 0 },
  { codigo: 8, codigoPorcentaje: 8, label: 'Exento de IVA',       tarifa: 0  }
]

export default {
  name: 'ItemForm',
  props: {
    codigo: { type: String, default: null }
  },
  data () {
    return {
      activeTab: 'general',
      tabs: [
        { id: 'general',       nombre: 'General'       },
        { id: 'clasificacion', nombre: 'Clasificación' },
        { id: 'precios',       nombre: 'Precios'       },
        { id: 'impuestos',     nombre: 'Impuestos'     }
      ],
      producto: {
        codigo_auxiliar: '',
        nombre: '',
        descripcion: '',
        unidad: 'UNIDAD',
        tipo: 'PRODUCTO',
        categoria: '',
        marca: '',
        costo: 0,
        utilidad: 0,
        precio_unitario: 0,
        precio_con_iva: 0,
        codigo_iva: 0,
        lleva_seriado: false,
        publicar_web: false,
        publicar_facebook: false,
        publicar_whatsapp: false,
        control_caducidad: false,
        estado: 'ACTIVO'
      },
      unidades: ['UNIDAD', 'KG', 'LIBRA', 'LITRO', 'METRO', 'CAJA', 'DOCENA', 'SERVICIO'],
      flags: [
        { campo: 'lleva_seriado',     nombre: 'Lleva Seriado'     },
        { campo: 'publicar_web',      nombre: 'Publicar en Web'   },
        { campo: 'publicar_facebook', nombre: 'Publicar Facebook' },
        { campo: 'publicar_whatsapp', nombre: 'Publicar WhatsApp' },
        { campo: 'control_caducidad', nombre: 'Control Caducidad' }
      ],
      impuestosOpciones: IMPUESTOS,
      loading: false,
      errores: {},
      feedback: { mensaje: '', tipo: '' }
    }
  },
  computed: {
    isEditMode () { return !!this.codigo },
    ivaSeleccionado () {
      return IMPUESTOS.find(i => i.codigo === this.producto.codigo_iva) || IMPUESTOS[0]
    },
    gananciaCalculada () {
      return ((this.producto.costo || 0) * (this.producto.utilidad || 0) / 100).toFixed(2)
    }
  },
  watch: {
    codigo: {
      immediate: true,
      handler (val) {
        if (val) this.cargarItem(val)
        else this.resetForm()
      }
    }
  },
  methods: {
    async cargarItem (codigo) {
      this.loading = true
      try {
        const data = await ApiService.get(`api/items/${codigo}`)
        this.producto = {
          ...this.producto,
          ...data,
          lleva_seriado:     !!data.lleva_seriado,
          publicar_web:      !!data.publicar_web,
          publicar_facebook: !!data.publicar_facebook,
          publicar_whatsapp: !!data.publicar_whatsapp,
          control_caducidad: !!data.control_caducidad,
          costo:             parseFloat(data.costo)        || 0,
          utilidad:          parseFloat(data.utilidad)     || 0,
          precio_unitario:   parseFloat(data.precio_unitario) || 0,
          precio_con_iva:    parseFloat(data.precio_con_iva)  || 0,
          codigo_iva:        data.codigo_iva !== undefined ? data.codigo_iva : 0
        }
      } catch (err) {
        console.error('Error al cargar item:', err)
        this.feedback = { mensaje: 'Error al cargar el item.', tipo: 'error' }
        this.$router.push('/panel/inventario')
      } finally {
        this.loading = false
      }
    },
    calcularPrecios () {
      const costo    = parseFloat(this.producto.costo)    || 0
      const utilidad = parseFloat(this.producto.utilidad) || 0
      const iva      = this.ivaSeleccionado.tarifa / 100

      this.producto.precio_unitario = parseFloat((costo + costo * utilidad / 100).toFixed(2))
      this.producto.precio_con_iva  = parseFloat((this.producto.precio_unitario * (1 + iva)).toFixed(2))
    },
    seleccionarImpuesto (imp) {
      this.producto.codigo_iva = imp.codigo
      this.calcularPrecios()
    },
    validar () {
      this.errores = {}
      if (!this.producto.nombre || !this.producto.nombre.trim()) {
        this.errores.nombre = 'El nombre es obligatorio.'
      }
      return Object.keys(this.errores).length === 0
    },
    async guardar () {
      if (!this.validar()) return

      this.loading = true
      this.feedback = { mensaje: '', tipo: '' }

      const payload = {
        ...this.producto,
        lleva_seriado:     this.producto.lleva_seriado     ? 1 : 0,
        publicar_web:      this.producto.publicar_web      ? 1 : 0,
        publicar_facebook: this.producto.publicar_facebook ? 1 : 0,
        publicar_whatsapp: this.producto.publicar_whatsapp ? 1 : 0,
        control_caducidad: this.producto.control_caducidad ? 1 : 0,
        tarifa_iva:        this.ivaSeleccionado.tarifa
      }

      try {
        let res
        if (this.isEditMode) {
          res = await ApiService.put(`api/items/${this.codigo}`, payload)
        } else {
          res = await ApiService.post('api/items', payload)
        }
        this.feedback = {
          mensaje: (res && res.mensaje) || 'Item guardado correctamente.',
          tipo: 'success'
        }
        setTimeout(() => this.$router.push('/panel/inventario'), 1500)
      } catch (err) {
        console.error('Error al guardar item:', err)
        const msg = err.response && err.response.data && err.response.data.mensaje
        this.feedback = {
          mensaje: msg ? `Error: ${msg}` : `Error al guardar: ${err.message}`,
          tipo: 'error'
        }
      } finally {
        this.loading = false
      }
    },
    cancelar () {
      this.$router.push('/panel/inventario')
    },
    resetForm () {
      this.producto = {
        codigo_auxiliar: '', nombre: '', descripcion: '',
        unidad: 'UNIDAD', tipo: 'PRODUCTO', categoria: '', marca: '',
        costo: 0, utilidad: 0, precio_unitario: 0, precio_con_iva: 0,
        codigo_iva: 0, lleva_seriado: false, publicar_web: false,
        publicar_facebook: false, publicar_whatsapp: false,
        control_caducidad: false, estado: 'ACTIVO'
      }
      this.errores = {}
      this.feedback = { mensaje: '', tipo: '' }
    }
  }
}
</script>

<style scoped>
.container {
  max-width: 960px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
}

/* Header */
.form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
}

.header-center { text-align: center; }

.header-right { width: 100px; }

.title {
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--color-primary);
  margin: 0 0 0.25rem 0;
}

.subtitle {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  font-family: monospace;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: none;
  border: 1px solid var(--color-border);
  color: var(--color-text-secondary);
  padding: 0.5rem 1rem;
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.btn-back:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

/* Tabs */
.tabs-bar {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: 2rem;
  overflow-x: auto;
}

.tab-btn {
  background: none;
  border: none;
  color: var(--color-text-secondary);
  padding: 0.875rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
  white-space: nowrap;
}

.tab-btn:hover { color: var(--color-text-primary); }

.tab-active {
  color: var(--color-primary);
  border-bottom-color: var(--color-primary);
}

/* Cards */
.card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
}

.mb { margin-bottom: 1.5rem; }

.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  color: var(--color-text-primary);
  font-size: 1rem;
  margin-bottom: 1.25rem;
}

/* Grid layouts */
.grid-2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.grid-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.full-width { grid-column: 1 / -1; }

/* Fields */
.field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.field label {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.field input,
.field select,
.field textarea {
  padding: 0.75rem;
  border-radius: var(--radius-sm);
  font-size: 0.95rem;
  border: 1px solid var(--color-border) !important;
  background: var(--color-surface-2) !important;
  color: var(--color-text-primary) !important;
  transition: border-color 0.2s;
}

.field input:focus,
.field select:focus,
.field textarea:focus {
  outline: none;
  border-color: var(--color-primary) !important;
}

.field textarea { resize: vertical; }

.field-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.field-highlight {
  color: var(--color-primary) !important;
  font-weight: 700;
  font-family: monospace;
}

.input-prefix, .input-suffix {
  display: flex;
  align-items: center;
  gap: 0;
}

.input-prefix span,
.input-suffix span {
  background: var(--color-surface-2) !important;
  border: 1px solid var(--color-border) !important;
  color: var(--color-text-secondary) !important;
  padding: 0.75rem 0.75rem;
  font-size: 0.9rem;
  font-weight: 700;
}

.input-prefix span { border-right: none !important; border-radius: var(--radius-sm) 0 0 var(--radius-sm); }
.input-suffix span { border-left: none !important; border-radius: 0 var(--radius-sm) var(--radius-sm) 0; }

.input-prefix input { border-radius: 0 var(--radius-sm) var(--radius-sm) 0; flex: 1; }
.input-suffix input { border-radius: var(--radius-sm) 0 0 var(--radius-sm); flex: 1; }

.field-error { color: var(--color-error); font-size: 0.8rem; }

/* Flags grid */
.flags-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
}

.flag-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1rem;
  border-radius: var(--radius-sm);
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  cursor: pointer;
}

.flag-name {
  font-size: 0.9rem;
  color: var(--color-text-primary);
  font-weight: 500;
}

/* Toggle */
.toggle-wrapper { position: relative; }
.toggle-input { position: absolute; opacity: 0; width: 0; height: 0; }

.toggle-track {
  width: 44px;
  height: 24px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  position: relative;
  transition: background 0.2s;
  cursor: pointer;
}

.toggle-input:checked + .toggle-track {
  background: var(--color-primary);
  border-color: var(--color-primary);
}

.toggle-thumb {
  width: 18px;
  height: 18px;
  background: var(--color-text-secondary);
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: transform 0.2s, background 0.2s;
}

.toggle-input:checked + .toggle-track .toggle-thumb {
  transform: translateX(20px);
  background: var(--color-bg);
}

/* Info box */
.info-box {
  background: var(--color-primary-dim);
  border: 1px solid var(--color-primary);
  border-radius: var(--radius-sm);
  padding: 0.75rem 1rem;
  color: var(--color-primary);
  font-size: 0.875rem;
  margin-top: 1rem;
}

/* Tax table */
.table-wrapper { overflow-x: auto; }

.items-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.items-table th {
  background: var(--color-surface-2);
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--color-text-secondary);
  border-bottom: 1px solid var(--color-border);
}

.text-center { text-align: center; }

.table-row {
  border-bottom: 1px solid var(--color-border);
  cursor: pointer;
  transition: background 0.2s;
}

.table-row:hover { background: var(--color-surface-2); }

.row-selected { background: var(--color-primary-dim) !important; }

.items-table td { padding: 0.75rem 1rem; color: var(--color-text-primary); }

.code-badge {
  background: var(--color-surface-2);
  color: var(--color-text-secondary);
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.85rem;
}

/* Footer */
.form-footer {
  display: flex;
  gap: 1rem;
  padding: 1.5rem 0;
  margin-top: 1rem;
  border-top: 1px solid var(--color-border);
}

.btn-save {
  background: var(--color-primary);
  color: var(--color-bg);
  border: none;
  padding: 0.875rem 2rem;
  border-radius: var(--radius-md);
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  box-shadow: var(--shadow-glow);
}

.btn-save:hover:not(:disabled) { transform: translateY(-2px); }
.btn-save:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-cancel {
  background: none;
  border: 1px solid var(--color-error);
  color: var(--color-error);
  padding: 0.875rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel:hover { background: rgba(239, 68, 68, 0.1); }

/* Feedback */
.feedback {
  padding: 1rem;
  border-radius: var(--radius-sm);
  font-weight: 600;
  text-align: center;
  margin-top: 1rem;
}

.feedback.success {
  background: var(--color-primary-dim);
  color: var(--color-primary);
  border: 1px solid var(--color-primary);
}

.feedback.error {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error);
  border: 1px solid var(--color-error);
}

.icon { width: 16px; height: 16px; stroke-width: 2; }

@media (max-width: 768px) {
  .container { padding: 1rem; }
  .grid-2, .grid-3 { grid-template-columns: 1fr; }
  .form-header { flex-direction: column; gap: 1rem; text-align: center; }
  .header-right { display: none; }
}
</style>



// WEBPACK FOOTER //
// src/components/Inventario/Item_form.vue